import apiService, { ApiError } from './apiService';

/**
 * Marketer Service
 * Handles all marketer-related API operations
 */
class MarketerService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of marketers with pagination
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer list response
   */
  async getMarketerList(params = {}, options = {}) {
    const { page = 1, size = 50, keyword, dateFrom, dateTo, order, status } = params;

    try {
      // Build request data
      const requestData = {
        page,
        size,
        order,
      };

      if (status) {
        requestData.status = status;
      }

      // Add keyword to payload if provided
      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      // Add dateFilter to payload if dates are provided
      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiNetRequest('/api/v1/account/list', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from marketer list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch marketer list:', error);

      // Re-throw with additional context for marketer-specific errors
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle unexpected errors
      throw new ApiError(
        'Failed to retrieve marketer list',
        0,
        'MARKETER_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get marketer details by ID
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer details response
   */
  async getMarketerDetails(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch marketer details for ID ${marketerId}:`,
        error
      );

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer details',
        0,
        'MARKETER_DETAILS_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Search marketers by criteria
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.query - Search query (name, email, etc.)
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMarketers(searchParams = {}, options = {}) {
    const { query, page = 1, size = 50 } = searchParams;

    try {
      // Note: Endpoint needs to be confirmed with backend team
      const response = await apiService.skaiNetRequest(
        '/api/v1/account/search',
        {
          method: 'POST',
          data: {
            query,
            page,
            size,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search marketers:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search marketers',
        0,
        'MARKETER_SEARCH_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Get marketer statistics and metrics
   * @param {string} marketerId - Marketer ID (optional, gets current user stats if not provided)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Marketer statistics response
   */
  async getMarketerStats(marketerId = null, options = {}) {
    try {
      const endpoint = marketerId
        ? `/api/v1/account/${marketerId}/stats`
        : '/api/v1/account/stats';

      const response = await apiService.skaiNetRequest(endpoint, {
        method: 'GET',
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch marketer statistics:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer statistics',
        0,
        'MARKETER_STATS_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Transform API marketer data to frontend format
   * @param {Object} apiMarketer - Raw marketer data from API
   * @returns {Object} Transformed marketer data
   */
  transformMarketerData(apiMarketer) {
    if (!apiMarketer) return null;

    const CAREER_LEVEL_LABELS = {
      0: '-',
      1: 'Level 1',
      2: 'Level 2',
      3: 'Level 3',
      4: 'Level 4',
      5: 'Level 5',
      6: 'Level 6',
      7: 'Level 7',
      8: 'Level 8',
    };

    /** BE account lifecycle codes; list excludes D (deleted). */
    const ACCOUNT_STATUS_LABELS = {
      A: 'Active',
      D: 'Deleted',
      S: 'Suspended',
      N: 'Inactive',
    };

    const accountStatusCode = apiMarketer.status;
    const accountStatusLabel =
      (accountStatusCode && ACCOUNT_STATUS_LABELS[accountStatusCode]) ||
      accountStatusCode ||
      '-';

    return {
      id: apiMarketer.id,
      accountId: apiMarketer.accountId,
      revealed: apiMarketer.revealed === true,
      firstName: apiMarketer.name?.split(' ')[0] || 'N/A',
      lastName: apiMarketer.name?.split(' ').slice(1).join(' ') || 'N/A',
      fullName: apiMarketer.name || 'N/A',
      companyName: apiMarketer.name || 'N/A',
      email: apiMarketer.email || 'N/A',
      phone: apiMarketer.phone || 'N/A',
      address: apiMarketer.address || '',
      province: apiMarketer.province || '',
      city: apiMarketer.city || '',
      zip: apiMarketer.zip || '',
      nationality: apiMarketer.nationality || '',
      language: apiMarketer.language || '',
      currency: apiMarketer.currency || '',
      plus: apiMarketer.plus ?? false,
      /** Plus/Free for subscription tier (UI "Account Type") */
      status: apiMarketer.plus ? 'Plus' : 'Basic',
      accountStatusCode,
      accountStatusLabel,
      joinDate: apiMarketer.createdAt
        ? new Date(apiMarketer.createdAt).toLocaleDateString('en-US')
        : 'N/A',
      referralCode: apiMarketer.referral || 'N/A',
      referral: apiMarketer.referral || 'N/A',
      referrer: apiMarketer.referrer || '',
      upline: apiMarketer.upline || 'N/A',
      downline: apiMarketer.downline || 0,
      downlineMarketer: apiMarketer.downlineMarketer || 0,
      downlinePartner: apiMarketer.downlinePartner || 0,
      downlineShopper: apiMarketer.downlineShopper || 0,
      partner: apiMarketer.partner || 0,
      careerPoint: apiMarketer.careerPoint || 0,
      generatedCareerPoint: apiMarketer.generatedCareerPoint ?? null,
      careerLevel: apiMarketer.careerLevel ?? 0,
      careerLevelLabel:
        CAREER_LEVEL_LABELS[apiMarketer.careerLevel] ??
        `Level ${apiMarketer.careerLevel}`,
      careerBonus: apiMarketer.careerBonus || 0,
      marketingBalance: apiMarketer.marketingBalance ?? apiMarketer.balance ?? 0,
      marketingBoost: apiMarketer.marketingBoost || 0,
      marketingBonusBalance: apiMarketer.marketingBonusBalance ?? 0,
      careerCommission: apiMarketer.careerCommision || 0,
      dateOfBirth: apiMarketer.dateOfBirth,
      paidStart: apiMarketer.paidStart,
      paidExpired: apiMarketer.paidExpired,
      bankAccountNumber: apiMarketer.bankAccountNumber || '',
      iban: apiMarketer.iban || '',
      beneficiaryName: apiMarketer.beneficiaryName || '',
      codiceFiscale: apiMarketer.codiceFiscale || apiMarketer.taxCode || '',
      // Marketer's CF address writes to MAIN address columns (not billing_*),
      // per release notes 5.3. So pre-fill aliases fall back to address/province/
      // city/zip rather than billingAddress/etc.
      codiceFiscaleAddress:
        apiMarketer.codiceFiscaleAddress || apiMarketer.address || '',
      codiceFiscaleProvince:
        apiMarketer.codiceFiscaleProvince || apiMarketer.province || '',
      codiceFiscaleCity:
        apiMarketer.codiceFiscaleCity || apiMarketer.city || '',
      codiceFiscaleZipCode:
        apiMarketer.codiceFiscaleZipCode || apiMarketer.zip || '',
      codiceFiscaleChecked: apiMarketer.codiceFiscaleChecked === true,
      codiceFiscaleCheckRawResponse:
        apiMarketer.codiceFiscaleCheckRawResponse || {},
      taxCode: apiMarketer.taxCode || '',
      vat: apiMarketer.vat || '',
      taxScheme: apiMarketer.taxScheme || '',
      billingAddress: apiMarketer.billingAddress || '',
      billingProvince: apiMarketer.billingProvince || '',
      billingCity: apiMarketer.billingCity || '',
      billingZipCode: apiMarketer.billingZipCode || '',
      billingPec: apiMarketer.billingPec || apiMarketer.billingPecEmail || '',
      billingPecEmail: apiMarketer.billingPecEmail || apiMarketer.billingPec || '',
      billingSdiCode: apiMarketer.billingSdiCode || '',
      billingBusinessName:
        apiMarketer.billingBusinessName || apiMarketer.businessName || '',
      billingTaxScheme: apiMarketer.billingTaxScheme || apiMarketer.taxScheme || '',
      billingPartitaIvaChecked:
        apiMarketer.billingPartitaIvaChecked === true ||
        apiMarketer.billingChecked === true,
      billingPartitaIvaRawResponse:
        apiMarketer.billingPartitaIvaRawResponse ||
        apiMarketer.billingCheckRawResponse ||
        {},
      // Partita Iva sub-section in detail page reads these aliases; they
      // fall back to the billing* values since BE stores them as one record.
      partitaIva: apiMarketer.partitaIva || apiMarketer.vat || '',
      partitaIvaAddress:
        apiMarketer.partitaIvaAddress || apiMarketer.billingAddress || '',
      partitaIvaProvince:
        apiMarketer.partitaIvaProvince || apiMarketer.billingProvince || '',
      partitaIvaCity:
        apiMarketer.partitaIvaCity || apiMarketer.billingCity || '',
      partitaIvaZipCode:
        apiMarketer.partitaIvaZipCode || apiMarketer.billingZipCode || '',
      emailVerified: apiMarketer.emailVerified,
      phoneVerified: apiMarketer.phoneVerified,
      twoFa: apiMarketer.twoFa,
      image: apiMarketer.image,
      cover: apiMarketer.cover,
      about: apiMarketer.about,
      quotas: apiMarketer.quotas || {
        shopper: { used: 0, total: 0 },
        marketer: { used: 0, total: 0 },
        partner: { used: 0, total: 0 },
      },
      hasDownline: (apiMarketer.downline || 0) > 0,
      deleted: apiMarketer.deleted || null,
      deletionReason: apiMarketer.deletionReason || '',
      unblockedAt: apiMarketer.unblockedAt || null,
      unblockedBy: apiMarketer.unblockedBy || '',
      unblockedByName: apiMarketer.unblockedByName || '',
      unblockReason: apiMarketer.unblockReason || '',
      createdAt: apiMarketer.createdAt,
      modifiedAt: apiMarketer.modifiedAt,
      modifiedBy: apiMarketer.modifiedBy,
      modifiedByName: apiMarketer.modifiedByName,
    };
  }

  /**
   * Get formatted marketer list with transformed data
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted marketer list response
   */
  async getFormattedMarketerList(params = {}, options = {}) {
    try {
      const response = await this.getMarketerList(params, options);

      // Transform the marketer data
      const transformedData = {
        ...response.data,
        data: response.data.data.map(marketer =>
          this.transformMarketerData(marketer)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted marketer list:', error);
      throw error;
    }
  }

  /**
   * Get formatted marketer details by ID
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted marketer details
   */
  async getFormattedMarketerDetails(marketerId, options = {}) {
    try {
      const response = await this.getMarketerDetails(marketerId, options);

      // Transform the marketer data
      const transformedData = this.transformMarketerData(response.data);

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error(
        `Failed to get formatted marketer details for ID ${marketerId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Create a new marketer
   * @param {Object} marketerData - Marketer data to create
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Created marketer response
   */
  async createMarketer(marketerData, options = {}) {
    try {
      const response = await apiService.skaiNetRequest('/api/v1/account', {
        method: 'POST',
        data: marketerData,
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to create marketer:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to create marketer',
        0,
        'MARKETER_CREATE_ERROR',
        { marketerData, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer information
   * @param {string} marketerId - Marketer ID
   * @param {Object} updateData - Data to update
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated marketer response
   */
  async updateMarketer(marketerId, updateData, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}`,
        {
          method: 'PUT',
          data: updateData,
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to update marketer ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to update marketer',
        0,
        'MARKETER_UPDATE_ERROR',
        { marketerId, updateData, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer/creator personal info (admin). All fields are optional —
   * only the fields you send get updated (partial update). Caller should diff
   * against the original to skip unchanged fields.
   * Endpoint: PUT /api/v1/account/admin/:id/personal-info
   *
   * `skipVerificationReset` (optional bool) preserves `phone_verified` /
   * `email_verified` flags when phone/email change; default behavior on BE
   * is to reset the flag on change.
   */
  async updatePersonalInfo(marketerId, payload, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    if (!payload || typeof payload !== 'object') {
      throw new ApiError(
        'Payload is required',
        400,
        'FeatureInvalidPayload'
      );
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/personal-info`,
        {
          method: 'PUT',
          data: payload,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update personal info for marketer ${marketerId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update personal info',
        0,
        'PERSONAL_INFO_UPDATE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Validate Partita Iva via OpenAPI (admin). Returns suggested billing
   * details for the admin to confirm. Does NOT mutate any data.
   * Endpoint: POST /api/v1/account/admin/:id/billing/validate
   */
  async validatePartitaIva(marketerId, partitaIva, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    const value = String(partitaIva ?? '').trim();
    if (!value) {
      throw new ApiError(
        'Partita Iva is required',
        400,
        'FeatureInvalidPayload'
      );
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/billing/validate`,
        {
          method: 'POST',
          data: { partitaIva: value },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to validate Partita Iva for marketer ${marketerId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to validate Partita Iva',
        0,
        'PARTITA_IVA_VALIDATE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer/creator billing details (admin). Triggers Stripe
   * customer sync + bank row update + marketer table update (Stripe-first).
   * Endpoint: PUT /api/v1/account/admin/:id/billing
   *
   * Note: validate response uses `billingTown` and `billingLegalCompanyName`
   * (merchant convention); the marketer PUT contract uses `billingCity` and
   * `billingBusinessName`. We normalize here so callers can stay merchant-style.
   */
  async updatePartitaIva(marketerId, payload, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    const trim = v => String(v ?? '').trim();
    const body = {
      billingPartitaIva: trim(payload?.billingPartitaIva),
      billingBusinessName: trim(
        payload?.billingBusinessName ?? payload?.billingLegalCompanyName
      ),
      billingAddress: trim(payload?.billingAddress),
      billingProvince: trim(payload?.billingProvince),
      billingCity: trim(payload?.billingCity ?? payload?.billingTown),
      billingZipCode: trim(payload?.billingZipCode),
      billingPecEmail: trim(payload?.billingPecEmail),
      billingSdiCode: trim(payload?.billingSdiCode),
      billingTaxScheme: trim(payload?.billingTaxScheme),
      billingPartitaIvaChecked: payload?.billingPartitaIvaChecked === true,
      billingPartitaIvaRawResponse: payload?.billingPartitaIvaRawResponse ?? {},
    };

    const required = [
      'billingPartitaIva',
      'billingAddress',
      'billingProvince',
      'billingCity',
      'billingZipCode',
    ];
    for (const key of required) {
      if (!body[key]) {
        throw new ApiError(
          `${key} is required`,
          400,
          'FeatureInvalidPayload'
        );
      }
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/billing`,
        {
          method: 'PUT',
          data: body,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update Partita Iva for marketer ${marketerId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update Partita Iva',
        0,
        'PARTITA_IVA_UPDATE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer/creator codice fiscale (admin). Triggers conditional
   * Stripe sync (skipped if marketer already has partita_iva — see release
   * notes 5.1) + bank row update (type=M) + marketer table update.
   * Endpoint: PUT /api/v1/account/admin/:id/codice-fiscale
   *
   * Note: the form uses merchant-style `billingTown` to keep autofill from
   * the (reused) Partita Iva validate response straightforward. We map it
   * to `billingCity` here for the PUT body.
   */
  async updateCodiceFiscale(marketerId, payload, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    const trim = v => String(v ?? '').trim();
    const body = {
      codiceFiscale: trim(payload?.codiceFiscale),
      billingAddress: trim(payload?.billingAddress),
      billingProvince: trim(payload?.billingProvince),
      billingCity: trim(payload?.billingCity ?? payload?.billingTown),
      billingZipCode: trim(payload?.billingZipCode),
      codiceFiscaleChecked: payload?.codiceFiscaleChecked === true,
      codiceFiscaleCheckRawResponse:
        payload?.codiceFiscaleCheckRawResponse ?? {},
    };

    const required = [
      'codiceFiscale',
      'billingAddress',
      'billingProvince',
      'billingCity',
      'billingZipCode',
    ];
    for (const key of required) {
      if (!body[key]) {
        throw new ApiError(
          `${key} is required`,
          400,
          'FeatureInvalidPayload'
        );
      }
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/codice-fiscale`,
        {
          method: 'PUT',
          data: body,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update Codice Fiscale for marketer ${marketerId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update Codice Fiscale',
        0,
        'CODICE_FISCALE_UPDATE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Update marketer/creator bank (admin) — IBAN + beneficiary name only.
   * BE upserts the type=M bank row; address/tax columns untouched.
   * Requires admin role A or S; reveal elevation NOT required (PUT itself
   * doesn't return masked data, but we should fetch fresh detail with
   * reveal active to pre-fill the form).
   */
  async updateMarketerBank(marketerId, payload, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    const iban = String(payload?.iban ?? '').trim();
    const beneficiaryName = String(payload?.beneficiaryName ?? '').trim();
    if (!iban || !beneficiaryName) {
      throw new ApiError(
        'IBAN and Beneficiary Name are required',
        400,
        'FeatureInvalidPayload'
      );
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/bank`,
        {
          method: 'PUT',
          data: { iban, beneficiaryName },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to update marketer bank ${marketerId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update marketer bank',
        0,
        'MARKETER_BANK_UPDATE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Delete/deactivate a marketer
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Delete response
   */
  async deleteMarketer(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}`,
        {
          method: 'DELETE',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to delete marketer ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to delete marketer',
        0,
        'MARKETER_DELETE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Get marketer downline list
   * @param {string} referrerAccountId - Account ID of the marketer whose downline we want to fetch
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Downline list response
   */
  async getMarketerDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const { page = 1, size = 50, order, ...additionalFilters } = params;

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        order,
        from: 'M',
        ...additionalFilters,
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/network',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || response.code !== 'OK') {
        throw new ApiError(
          'Invalid response format from downline list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      // BE may return { code: "OK", data: null } for empty/edge cases; normalize to pagination shape.
      if (response.data == null) {
        return {
          ...response,
          data: {
            page,
            pageCount: 1,
            size,
            count: 0,
            total: 0,
            metadata: {},
            data: [],
          },
        };
      }

      if (typeof response.data !== 'object') {
        throw new ApiError(
          'Invalid response format from downline list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      const inner = response.data;
      if (!Array.isArray(inner.data)) {
        return {
          ...response,
          data: {
            ...inner,
            data: [],
          },
        };
      }

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch downline for account ${referrerAccountId}:`,
        error
      );

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer downline',
        0,
        'MARKETER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Get shopper downline list (POST /api/v1/account/shoppers).
   * `from` is required by BE; omitted here defaults to `S` (Shopper referrer).
   * Mapping per referrer type: Creator/Marketer `M`, Shopper `S`, Merchant/Partner `P`.
   * Also supported: accountId (set from referrerAccountId), page, size, productionMonth (YYYY-MM),
   * rootId, order (accepted; shoppers endpoint may not apply sort yet).
   */
  async getShopperDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const {
      page = 1,
      size = 50,
      order,
      from: fromParam,
      ...additionalFilters
    } = params;

    const from = fromParam ?? 'S';

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        order,
        from,
        ...additionalFilters,
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/shoppers',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from shopper downline API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch shopper downline for account ${referrerAccountId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to retrieve shopper downline',
        0,
        'SHOPPER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Merchant/partner downline list (POST /api/v1/account/partners).
   * Not the same as `/account/shoppers` — BE uses this for merchant-type downline.
   * `from` is required by BE; omitted here defaults to `M` (referrer is Creator/Marketer).
   * Pass other `from` values only when BE specifies them for a different admin context.
   */
  async getPartnerDownline(referrerAccountId, params = {}, options = {}) {
    if (!referrerAccountId) {
      throw new ApiError(
        'Referrer Account ID is required',
        400,
        'MISSING_REFERRER_ACCOUNT_ID'
      );
    }

    const {
      page = 1,
      size = 50,
      order,
      from: fromParam,
      ...additionalFilters
    } = params;

    const from = fromParam ?? 'M';

    try {
      const requestData = {
        page,
        size,
        accountId: referrerAccountId,
        order,
        from,
        ...additionalFilters,
      };

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/partners',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from partner downline API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch partner downline for account ${referrerAccountId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to retrieve partner downline',
        0,
        'PARTNER_DOWNLINE_ERROR',
        { referrerAccountId, originalError: error.message }
      );
    }
  }

  /**
   * Get formatted shopper downline list
   */
  async getFormattedShopperDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getShopperDownline(
        referrerAccountId,
        params,
        options
      );
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      const transformedData = {
        ...response.data,
        data: rows.map(marketer => this.transformMarketerData(marketer)),
      };
      return { ...response, data: transformedData };
    } catch (error) {
      console.error('Failed to get formatted shopper downline:', error);
      throw error;
    }
  }

  /**
   * Get formatted partner downline list
   */
  async getFormattedPartnerDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getPartnerDownline(
        referrerAccountId,
        params,
        options
      );
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      const transformedData = {
        ...response.data,
        data: rows.map(marketer => this.transformMarketerData(marketer)),
      };
      return { ...response, data: transformedData };
    } catch (error) {
      console.error('Failed to get formatted partner downline:', error);
      throw error;
    }
  }

  /**
   * Get formatted marketer downline with transformed data
   * @param {string} referrerAccountId - Account ID of the marketer whose downline we want to fetch
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 50)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted downline list response
   */
  async getFormattedMarketerDownline(
    referrerAccountId,
    params = {},
    options = {}
  ) {
    try {
      const response = await this.getMarketerDownline(
        referrerAccountId,
        params,
        options
      );

      const rows = Array.isArray(response.data.data) ? response.data.data : [];

      const transformedData = {
        ...response.data,
        data: rows.map(marketer => this.transformMarketerData(marketer)),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted marketer downline:', error);
      throw error;
    }
  }

  /**
   * Export marketer list - now returns JSON data instead of CSV
   * @param {Object} params - Filtering parameters
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Export information as JSON
   */
  async exportMarketerList(params = {}, options = {}) {
    const { keyword, dateFrom, dateTo, order } = params;

    try {
      const requestData = { order };

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiNetRequest(
        '/api/v1/account/list-export',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      // Backend now returns JSON instead of CSV
      if (!response.data || typeof response.data !== 'object') {
        throw new ApiError(
          'Invalid response format from marketer export API. Expected JSON object.',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to export marketer list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to export marketer list',
        0,
        'MARKETER_EXPORT_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get marketer downline/upline tree
   * @param {string} marketerId - Marketer ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Tree structure response
   */
  async getMarketerTree(marketerId, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }

    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/${marketerId}/tree`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to fetch marketer tree for ${marketerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve marketer tree',
        0,
        'MARKETER_TREE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Immediately delete creator/marketer account (admin). Requires admin reason and password.
   * Endpoint: POST /api/v1/account/admin/:id/delete
   */
  async deleteMarketerAdmin(marketerId, { reason, password }, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    const trimReason = String(reason || '').trim();
    const trimPassword = String(password || '').trim();
    if (!trimReason || !trimPassword) {
      throw new ApiError(
        'Reason and password are required',
        400,
        'FeatureInvalidPayload'
      );
    }
    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/delete`,
        {
          method: 'POST',
          data: { reason: trimReason, password: trimPassword },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to delete marketer ${marketerId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to delete marketer account',
        0,
        'MARKETER_DELETE_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }

  /**
   * Unblock a deleted creator/marketer account (admin) to lift 6-month credential re-registration block.
   * Requires admin reason and password.
   * Endpoint: POST /api/v1/account/admin/:id/unblock
   */
  async unblockMarketerAdmin(marketerId, { reason, password }, options = {}) {
    if (!marketerId) {
      throw new ApiError('Marketer ID is required', 400, 'MISSING_MARKETER_ID');
    }
    const trimReason = String(reason || '').trim();
    const trimPassword = String(password || '').trim();
    if (!trimReason || !trimPassword) {
      throw new ApiError(
        'Reason and password are required',
        400,
        'FeatureInvalidPayload'
      );
    }
    try {
      const response = await apiService.skaiNetRequest(
        `/api/v1/account/admin/${marketerId}/unblock`,
        {
          method: 'POST',
          data: { reason: trimReason, password: trimPassword },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to unblock marketer ${marketerId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to unblock marketer account',
        0,
        'MARKETER_UNBLOCK_ERROR',
        { marketerId, originalError: error.message }
      );
    }
  }
}

// Create singleton instance
const marketerService = new MarketerService();

export default marketerService;
export { MarketerService };
